import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, errMsg, fmtMoney, fmtDate } from "@/lib/api";
import { Country, State, City } from "country-state-city";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CreditCard, Wallet, Banknote, CalendarX2, CheckCircle2, ArrowLeft, ArrowRight, FileText, ShieldCheck, Users, Luggage, Cog, Baby, Landmark } from "lucide-react";

const AIRPORT = "Lynden Pindling Intl Airport (NAS)";
const PICKUP_LOCATIONS = [
  "Easy Drive Office — West Bay Street",
  "Lynden Pindling Intl Airport (NAS)",
  "Downtown Nassau",
  "Cable Beach",
  "Paradise Island",
  "Prince George Wharf (Cruise Port)",
  "Hotel / Villa Delivery",
];
const LOCATIONS = PICKUP_LOCATIONS;

const overlaps = (s, e, r) => s < r.end && r.start < e;
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Booking() {
  const { vehicleId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [unavail, setUnavail] = useState({ blackouts: [], booked: [] });
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [method, setMethod] = useState("card");
  const [wireEnabled, setWireEnabled] = useState(true);
  useEffect(() => { api.get("/payment-options").then((r) => setWireEnabled(r.data.wire_enabled !== false)).catch(() => {}); }, []);
  useEffect(() => { if (!wireEnabled && method === "wire") setMethod("card"); }, [wireEnabled]); // eslint-disable-line
  const [result, setResult] = useState(null);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [renterType, setRenterType] = useState("international");
  const [passportFile, setPassportFile] = useState(null);
  const [licenseFront, setLicenseFront] = useState(null);
  const [licenseBack, setLicenseBack] = useState(null);
  const [nibFile, setNibFile] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [referral, setReferral] = useState(null);

  const applyReferral = async () => {
    if (!referralCode.trim()) return;
    try {
      const { data } = await api.get("/referrals/validate", { params: { code: referralCode.trim(), email: form.customer_email } });
      setReferral(data);
      toast.success(`Referral applied — $${data.discount} off, thanks to ${data.from || "a member"}!`);
    } catch (e) {
      setReferral(null);
      toast.error(errMsg(e));
    }
  };
  const [cardPhoto, setCardPhoto] = useState(null);
  const [babySeat, setBabySeat] = useState(false);
  const [gps, setGps] = useState(false);
  const [roadside, setRoadside] = useState(false);
  const [sli, setSli] = useState(true);
  const [driverDetails, setDriverDetails] = useState([]);
  const [additionalDrivers, setAdditionalDrivers] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [form, setForm] = useState({
    start_date: "",
    end_date: "",
    pickup_location: LOCATIONS[0],
    dropoff_location: LOCATIONS[0],
    pickup_time: "10:00",
    dropoff_time: "10:00",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    flight_number: "",
    passport_number: "",
    notes: "",
    renter_country: "",
    renter_state: "",
    renter_city: "",
    address_line: "",
    referral_code: "",
  });

  useEffect(() => {
    api.get(`/vehicles/${vehicleId}`).then((r) => setVehicle(r.data)).catch(() => toast.error("Vehicle not found"));
    api.get(`/vehicles/${vehicleId}/unavailability`).then((r) => setUnavail(r.data)).catch(() => {});
  }, [vehicleId]);

  useEffect(() => {
    const q = Object.fromEntries(searchParams.entries());
    if (Object.keys(q).length) {
      setForm((f) => ({
        ...f,
        start_date: q.pickup_date || f.start_date,
        end_date: q.dropoff_date || f.end_date,
        pickup_location: q.pickup_location || f.pickup_location,
        dropoff_location: q.dropoff_location || f.dropoff_location,
        pickup_time: q.pickup_time || f.pickup_time,
        dropoff_time: q.dropoff_time || f.dropoff_time,
      }));
      if (q.coupon) {
        const code = q.coupon.toUpperCase();
        setCouponCode(code);
        api.post("/coupons/validate", { code }).then((r) => setCoupon(r.data)).catch(() => {});
      }
      if (user?.email) {
        api.get("/loyalty/status", { params: { email: user.email } }).then((r) => {
          setLoyalty(r.data);
          if (r.data.percent > 0) setCoupon((cur) => (cur && cur.percent >= r.data.percent ? cur : { code: r.data.tier, percent: r.data.percent, description: "Loyalty reward", loyalty: true }));
        }).catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        customer_email: f.customer_email || user.email || "",
        customer_phone: f.customer_phone || user.phone || "",
      }));
    }
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const days = useMemo(() => {
    if (!form.start_date || !form.end_date) return 0;
    return Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000);
  }, [form.start_date, form.end_date]);

  const [pricing, setPricing] = useState({ rules: [], weekend_premium_pct: 0 });
  useEffect(() => { api.get("/pricing/rules").then((r) => setPricing(r.data)).catch(() => {}); }, []);
  const baseTotal = useMemo(() => {
    if (!vehicle || days <= 0) return 0;
    let total = 0;
    const s = new Date(form.start_date + "T00:00:00");
    for (let i = 0; i < days; i++) {
      const d = new Date(s);
      d.setDate(s.getDate() + i);
      const ds = d.toISOString().slice(0, 10);
      let rate = vehicle.price_per_day;
      pricing.rules.forEach((r) => { if (r.start_date <= ds && ds <= r.end_date) rate *= 1 + r.percent / 100; });
      const hsPct = pricing.high_season_enabled ? (pricing.high_season_pct || 0) : 0;
      const mon = d.getMonth() + 1;
      if (hsPct && (mon === 12 || mon <= 4)) rate *= 1 + hsPct / 100;
      const dow = d.getDay();
      if ((dow === 0 || dow === 6) && pricing.weekend_premium_pct) rate *= 1 + pricing.weekend_premium_pct / 100;
      total += rate;
    }
    return Math.round(total * 100) / 100;
  }, [vehicle, days, form.start_date, pricing]);
  const babySeatFee = babySeat && days > 0 && days < 12 && loyalty?.tier !== "Platinum Islander" ? days * (loyalty?.tier === "Gold Voyager" ? 3.5 : 7) : 0;
  const gpsFee = gps && days > 0 && loyalty?.tier !== "Platinum Islander" ? days * (loyalty?.tier === "Gold Voyager" ? 2.5 : 5) : 0;
  const driverFee = loyalty?.tier === "Platinum Islander" ? 0 : loyalty?.tier === "Gold Voyager" ? additionalDrivers * 12.5 : additionalDrivers * 25;
  const roadsideFee = roadside && days > 0 ? days * 15 : 0;
  const sliFee = sli && days > 0 ? Math.round(days * 7.99 * 100) / 100 : 0;
  const isBuddy = coupon?.kind === "flat_rate" && Number(coupon?.daily_rate) > 0;
  const baseTotalEff = isBuddy ? Math.round(days * Number(coupon.daily_rate) * 100) / 100 : baseTotal;
  const subtotal = baseTotalEff + babySeatFee + gpsFee + driverFee + roadsideFee + sliFee;
  const weeklyDiscount = !isBuddy && days >= 7 ? baseTotalEff * 0.1 : 0;
  const referralDiscount = referral ? referral.discount : 0;
  const discounted = subtotal - weeklyDiscount - referralDiscount;
  const discountAmount = coupon && !isBuddy ? discounted * (coupon.percent / 100) : 0;
  const processingFee = (discounted - discountAmount) * 0.05;
  const vatAmount = (discounted - discountAmount + processingFee) * 0.10;
  const airportFee = form.dropoff_location === AIRPORT ? 25 : 0;
  const isPlatinum = loyalty?.tier === "Platinum Islander";
  const depositWaived = coupon?.kind === "waive_deposit" || isBuddy;
  const DEPOSIT = isPlatinum ? 100 : 150;
  const total = discounted - discountAmount + vatAmount + processingFee + airportFee + (depositWaived ? 0 : DEPOSIT);

  const renterCountries = useMemo(() => Country.getAllCountries(), []);
  useEffect(() => {
    if (!user?.email) return;
    setForm((f) => ({ ...f, customer_name: f.customer_name || user.name || "", customer_email: f.customer_email || user.email }));
    api.get("/bookings/last-details", { params: { email: user.email } })
      .then(({ data }) => {
        if (!data.found) return;
        const iso = renterCountries.find((c) => c.name === data.renter_country)?.isoCode || "";
        const stateIso = iso ? (State.getStatesOfCountry(iso).find((s) => s.name === data.renter_state)?.isoCode || "") : "";
        setForm((f) => ({
          ...f,
          customer_name: f.customer_name || data.customer_name || "",
          customer_phone: f.customer_phone || data.customer_phone || "",
          renter_country: iso || f.renter_country,
          renter_state: stateIso || f.renter_state,
          renter_city: data.renter_city || f.renter_city,
          address_line: data.address_line || f.address_line,
          passport_number: f.passport_number || data.passport_number || "",
        }));
        if (data.renter_type) setRenterType(data.renter_type);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const renterStates = form.renter_country ? State.getStatesOfCountry(form.renter_country) : [];
  const renterCities = form.renter_country
    ? form.renter_state
      ? City.getCitiesOfState(form.renter_country, form.renter_state)
      : renterStates.length === 0
        ? City.getCitiesOfCountry(form.renter_country)
        : []
    : [];
  const localMinBlocked = renterType === "local" && days > 0 && days < 2;

  const blockedRanges = [...(unavail.blackouts || []), ...(unavail.booked || [])];
  const conflict =
    form.start_date && form.end_date && days > 0
      ? blockedRanges.find((r) => overlaps(form.start_date, form.end_date, r))
      : null;
  const saturdayPickup = !!form.start_date && new Date(form.start_date + "T00:00:00").getDay() === 6;

  const step1Valid = form.start_date && form.end_date && days > 0 && !conflict && !saturdayPickup;
  const docsValid =
    renterType === "international"
      ? passportFile && form.passport_number.trim() && form.flight_number.trim() && licenseFront && licenseBack
      : licenseFront && licenseBack && nibFile && cardPhoto;
  const driverSlotsValid = Array.from({ length: additionalDrivers }, (_, i) => driverDetails[i] || {}).every(
    (d) => d.name?.trim() && /\S+@\S+\.\S+/.test(d.email || "") && d.license_number?.trim() &&
      (renterType !== "international" || (d.passport_number?.trim() && d.flight_number?.trim()))
  );
  const step2Valid =
    form.customer_name.trim() && /\S+@\S+\.\S+/.test(form.customer_email) && form.customer_phone.trim() &&
    form.renter_country && form.renter_city && form.address_line.trim() && (renterStates.length === 0 || form.renter_state) &&
    docsValid && ageConfirmed && !localMinBlocked && driverSlotsValid;

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: couponCode.trim() });
      const best = loyalty && loyalty.percent > data.percent
        ? { code: loyalty.tier, percent: loyalty.percent, description: "Loyalty reward", loyalty: true }
        : data;
      setCoupon(best);
      toast.success(best.loyalty ? `Loyalty ${best.code} kept — ${best.percent}% off (beats the code)` : best.kind === "flat_rate" ? `Buddy pass ${best.code} applied — ${fmtMoney(best.daily_rate)}/day, deposit waived` : `Discount code ${best.code} applied — ${best.percent}% off`);
    } catch (e) {
      setCoupon(null);
      toast.error(errMsg(e));
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        ...form,
        renter_country: renterCountries.find((c) => c.isoCode === form.renter_country)?.name || form.renter_country,
        renter_state: renterStates.find((s) => s.isoCode === form.renter_state)?.name || form.renter_state,
        address_line: form.address_line.trim(),
        vehicle_id: vehicle.id,
        origin_url: window.location.origin,
        renter_type: renterType,
        referral_code: referral ? referralCode.trim().toUpperCase() : "",
        baby_seat: babySeat,
        gps: gps,
        roadside: roadside,
        sli: sli,
        referral_code: form.referral_code,
        additional_drivers: additionalDrivers,
        additional_driver_details: driverDetails.slice(0, additionalDrivers),
        coupon_code: coupon?.loyalty ? "" : coupon?.code || "",
      });
      localStorage.setItem(`booking_email_${booking.id}`, form.customer_email);
      localStorage.setItem("ed_visitor", JSON.stringify({ name: form.customer_name, email: form.customer_email }));
      const uploads =
        renterType === "international"
          ? [["passport", passportFile], ["license_front", licenseFront], ["license_back", licenseBack]]
          : [["license_front", licenseFront], ["license_back", licenseBack], ["nib_card", nibFile], ["credit_card", cardPhoto]];
      for (const [docType, file] of uploads) {
        if (!file) continue;
        try {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("email", form.customer_email);
          fd.append("doc_type", docType);
          await api.post(`/bookings/${booking.id}/license`, fd);
        } catch (e2) {
          toast.warning(`Booking saved, but the ${docType.replace(/_/g, " ")} upload failed — send it to us on WhatsApp.`);
        }
      }
      if (method === "card" || method === "paypal") {
        const { data } = await api.post(`/bookings/${booking.id}/pay/${method === "card" ? "stripe" : "paypal"}`, {
          origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
      } else {
        const { data } = await api.post(`/bookings/${booking.id}/pay/manual`, { method });
        setResult({ booking, instructions: data.instructions });
        toast.success("Booking created — complete payment to confirm");
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!vehicle)
    return <div className="pt-40 pb-24 text-center text-muted-foreground font-ui">Loading vehicle…</div>;

  if (result) {
    const { booking, instructions } = result;
    return (
      <div data-testid="booking-manual-confirmation" className="max-w-2xl mx-auto px-6 pt-32 pb-24">
        <CheckCircle2 size={40} className="gold-text" />
        <h1 className="mt-6 font-display text-4xl sm:text-5xl tracking-tight">
          Booking <span className="italic gold-text">reserved</span>
        </h1>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          Reference <span data-testid="booking-ref" className="gold-text font-ui font-semibold">{booking.booking_ref}</span> ·{" "}
          {vehicle.name} · {fmtDate(booking.start_date)} → {fmtDate(booking.end_date)} ·{" "}
          <span className="gold-text">{fmtMoney(booking.total)}</span>
        </p>
        <div className="mt-8 bg-card border border-primary/30 rounded-2xl p-8">
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-primary">
            Complete your payment via {{ paypal: "PayPal", manual_card: "Manual Card", wire: "Bank Wire Transfer", zelle: "Zelle" }[instructions.method] || "Zelle"}
          </p>
          <p data-testid="manual-payment-instructions" className="mt-4 text-sm leading-relaxed text-foreground/90 whitespace-pre-line">
            {instructions.message}
          </p>
          <p className="mt-4 text-xs text-muted-foreground">
            Send to: <span className="gold-text font-ui">{instructions.send_to}</span>
            {instructions.phone ? ` · ${instructions.phone}` : ""}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Your booking is confirmed by email as soon as payment is verified (usually within a few hours).
          </p>
        </div>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link to={`/contract/${booking.id}`}>
            <Button data-testid="view-contract-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui">
              <FileText size={15} className="mr-2" /> View & Sign Contract
            </Button>
          </Link>
          <Link to="/fleet">
            <Button variant="outline" className="rounded-full border-border bg-transparent hover:bg-black/5 hover:text-primary font-ui">
              Back to Fleet
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="max-w-6xl mx-auto px-6 lg:px-10 pt-32 pb-24">
      <Link to="/fleet" className="text-sm font-ui text-muted-foreground hover:text-primary transition-colors duration-200 flex items-center gap-2">
        <ArrowLeft size={15} /> Back to fleet
      </Link>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <div className="rounded-2xl overflow-hidden border border-border">
            <img src={vehicle.image_url} alt={vehicle.name} className="w-full aspect-[4/3] object-cover" />
          </div>
          <h1 className="mt-6 font-display text-4xl tracking-tight">{vehicle.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-muted-foreground font-ui">
            <span className="text-[11px] uppercase tracking-[0.15em] text-primary">{vehicle.category}</span>
            <span className="flex items-center gap-1.5"><Users size={14} className="gold-text" /> {vehicle.seats} seats</span>
            <span data-testid="booking-vehicle-luggage" className="flex items-center gap-1.5"><Luggage size={14} className="gold-text" /> Fits {vehicle.luggage ?? 0} bags</span>
            <span className="flex items-center gap-1.5"><Cog size={14} className="gold-text" /> {vehicle.transmission}</span>
          </div>
          <p className="mt-2 gold-text font-ui text-xl font-semibold">{fmtMoney(vehicle.price_per_day)} / day</p>
          <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{vehicle.description}</p>

          {blockedRanges.length > 0 && (
            <div data-testid="unavailable-dates-panel" className="mt-6 bg-card border border-border rounded-2xl p-5">
              <p className="flex items-center gap-2 text-xs font-ui tracking-[0.2em] uppercase text-muted-foreground">
                <CalendarX2 size={14} className="gold-text" /> Unavailable dates
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {blockedRanges.map((r, i) => (
                  <span key={i} className="text-xs font-ui bg-black/5 border border-border rounded-full px-3 py-1.5 text-muted-foreground">
                    {fmtDate(r.start)} → {fmtDate(r.end)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-3">
          <div className="flex items-center gap-3 mb-10">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-3">
                <div
                  data-testid={`booking-step-indicator-${s}`}
                  className={`w-8 h-8 rounded-full border flex items-center justify-center text-xs font-ui ${
                    step >= s ? "border-primary gold-text" : "border-border text-muted-foreground"
                  }`}
                >
                  {s}
                </div>
                <span className={`text-xs font-ui tracking-wide ${step >= s ? "text-foreground" : "text-muted-foreground"}`}>
                  {s === 1 ? "Dates" : s === 2 ? "Details" : "Payment"}
                </span>
                {s < 3 && <div className="w-8 h-px bg-white/15" />}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div data-testid="booking-step-1" className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label className="font-ui text-sm tracking-wide text-muted-foreground">Pickup date</Label>
                  <Input data-testid="booking-start-date" type="date" min={todayISO()} value={form.start_date} onChange={set("start_date")} className="input-dark mt-2" />
                </div>
                <div>
                  <Label className="font-ui text-sm tracking-wide text-muted-foreground">Return date</Label>
                  <Input data-testid="booking-end-date" type="date" min={form.start_date || todayISO()} value={form.end_date} onChange={set("end_date")} className="input-dark mt-2" />
                </div>
                <div>
                  <Label className="font-ui text-sm tracking-wide text-muted-foreground">Pickup time</Label>
                  <Input data-testid="booking-pickup-time" type="time" value={form.pickup_time} onChange={set("pickup_time")} className="input-dark mt-2" />
                </div>
                <div>
                  <Label className="font-ui text-sm tracking-wide text-muted-foreground">Drop-off time</Label>
                  <Input data-testid="booking-dropoff-time" type="time" value={form.dropoff_time} onChange={set("dropoff_time")} className="input-dark mt-2" />
                </div>
                <div>
                  <Label className="font-ui text-sm tracking-wide text-muted-foreground">Pickup location</Label>
                  <select data-testid="booking-pickup-location" value={form.pickup_location} onChange={set("pickup_location")} className="mt-2 w-full h-10 rounded-md input-dark border px-3 text-sm bg-card">
                    {PICKUP_LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="font-ui text-sm tracking-wide text-muted-foreground">Drop-off location</Label>
                  <select data-testid="booking-dropoff-location" value={form.dropoff_location} onChange={set("dropoff_location")} className="mt-2 w-full h-10 rounded-md input-dark border px-3 text-sm bg-card">
                    {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                  {form.dropoff_location === AIRPORT && (
                    <p data-testid="airport-dropoff-notice" className="mt-2 text-xs text-amber-600 font-ui">Airport drop-off: a $25 fee is automatically charged on your invoice</p>
                  )}
                </div>
              </div>
              {days >= 12 && (
                <div
                  data-testid="booking-free-baby-seat-callout"
                  className="flex items-center gap-3 rounded-xl border border-emerald-300 bg-emerald-50 px-5 py-4"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                    <Baby size={17} />
                  </span>
                  <p className="text-sm font-ui text-emerald-800">
                    <span className="font-semibold">You qualify for a FREE baby seat!</span>{" "}
                    Your {days}-day rental includes a complimentary baby / child car seat — {babySeat ? "added to your booking below." : "just tick the box below to add it."}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-start gap-3 bg-card border border-border rounded-xl p-4">
                  <Checkbox data-testid="booking-baby-seat" checked={babySeat} onCheckedChange={(v) => setBabySeat(!!v)} className="mt-0.5 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                  <div>
                    <p className="text-sm font-ui font-semibold">Baby / child car seat</p>
                    <p className="text-xs text-muted-foreground">+$7 per day — <span className="text-emerald-600 font-medium">FREE on rentals of 12+ days</span> — installed before pickup</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-card border border-border rounded-xl p-4">
                  <Checkbox data-testid="booking-gps" checked={gps} onCheckedChange={(v) => setGps(!!v)} className="mt-0.5 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                  <div>
                    <p className="text-sm font-ui font-semibold">GPS navigation unit</p>
                    <p className="text-xs text-muted-foreground">+$5 per day — island maps preloaded</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-card border border-border rounded-xl p-4">
                  <Checkbox data-testid="booking-roadside" checked={roadside} onCheckedChange={(v) => setRoadside(!!v)} className="mt-0.5 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                  <div>
                    <p className="text-sm font-ui font-semibold">24/7 roadside assistance</p>
                    <p className="text-xs text-muted-foreground">+$15 per day — flat tires, battery jumps, lockouts & towing support. Add it now, or mid-rental from your account (activates after a short waiting period).</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-card border border-border rounded-xl p-4">
                  <Checkbox data-testid="booking-sli" checked={sli} onCheckedChange={(v) => setSli(!!v)} className="mt-0.5 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                  <div>
                    <p className="text-sm font-ui font-semibold">Supplementary Liability Insurance (SLI) <span className="ml-1 inline-block rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold px-2 py-0.5 align-middle">Recommended</span></p>
                    <p className="text-xs text-muted-foreground">+$7.99 per day — third-party liability coverage with a $500 deductible per incident</p>
                  </div>
                </div>
                <div className="bg-card border border-border rounded-xl p-4">
                  <Label className="font-ui text-sm tracking-wide text-muted-foreground">Additional drivers (+$25 each)</Label>
                  <select data-testid="booking-additional-drivers" value={additionalDrivers} onChange={(e) => setAdditionalDrivers(Number(e.target.value))} className="mt-2 w-full h-10 rounded-md input-dark border px-3 text-sm bg-card">
                    {[0, 1, 2, 3].map((n) => (
                      <option key={n} value={n}>{n === 0 ? "None" : `${n} driver${n > 1 ? "s" : ""} — +$${25 * n}`}</option>
                    ))}
                  </select>
                  {additionalDrivers > 0 && (
                    <p className="mt-1.5 text-xs text-muted-foreground">Each additional driver must be 25+ and present their driver's license on the day of rental.</p>
                  )}
                </div>
              </div>
              {conflict && (
                <p data-testid="booking-conflict-error" className="text-sm text-destructive font-ui">
                  These dates overlap an existing booking or blackout ({fmtDate(conflict.start)} → {fmtDate(conflict.end)}). Please choose different dates.
                </p>
              )}
              {days > 0 && !conflict && (
                <div data-testid="booking-price-summary" className="bg-card border border-border rounded-2xl p-6 space-y-2">
                  <p data-testid="booking-quote-specs" className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-ui text-muted-foreground pb-1">
                    <span className="font-semibold text-foreground">{vehicle.name}</span>
                    <span className="flex items-center gap-1.5"><Users size={12} className="gold-text" /> {vehicle.seats} seats</span>
                    <span className="flex items-center gap-1.5"><Luggage size={12} className="gold-text" /> Fits {vehicle.luggage ?? 0} bags</span>
                  </p>
                  <div className="flex items-center justify-between text-sm font-ui"><span className="text-muted-foreground">{days} {days === 1 ? "day" : "days"} × {fmtMoney(vehicle.price_per_day)}</span><span>{fmtMoney(baseTotal)}</span></div>
                  {pricing.high_season_enabled && days > 0 && ([12, 1, 2, 3, 4].includes(new Date(form.start_date + "T00:00:00").getMonth() + 1) || [12, 1, 2, 3, 4].includes(new Date(form.end_date + "T00:00:00").getMonth() + 1)) && (
                    <p data-testid="booking-high-season-note" className="text-[11px] font-ui text-amber-600 -mt-1">High season (Dec–Apr): +{pricing.high_season_pct || 15}% seasonal rate applied to in-season days</p>
                  )}
                  {babySeat && <div className="flex items-center justify-between text-sm font-ui"><span className="text-muted-foreground">Baby car seat</span><span>{days >= 12 ? <span className="text-emerald-600 font-semibold">FREE — 12+ day perk</span> : fmtMoney(babySeatFee)}</span></div>}
                  {gps && <div className="flex items-center justify-between text-sm font-ui"><span className="text-muted-foreground">GPS navigation</span><span>{fmtMoney(gpsFee)}</span></div>}
                  {roadside && <div data-testid="booking-roadside-row" className="flex items-center justify-between text-sm font-ui"><span className="text-muted-foreground">24/7 roadside assistance</span><span>{fmtMoney(roadsideFee)}</span></div>}
                  {sli && <div data-testid="booking-sli-row" className="flex items-center justify-between text-sm font-ui"><span className="text-muted-foreground">SLI — liability insurance ($500 deductible)</span><span>{fmtMoney(sliFee)}</span></div>}
                  {additionalDrivers > 0 && <div className="flex items-center justify-between text-sm font-ui"><span className="text-muted-foreground">Additional drivers × {additionalDrivers}</span><span>{fmtMoney(driverFee)}</span></div>}
                  {weeklyDiscount > 0 && <div data-testid="booking-weekly-discount" className="flex items-center justify-between text-sm font-ui"><span className="text-emerald-600">Weekly discount — 7+ days (−10%)</span><span className="text-emerald-600">−{fmtMoney(weeklyDiscount)}</span></div>}
                  {coupon && (coupon.kind === "waive_deposit"
                    ? <div data-testid="deposit-waived-side" className="flex items-center justify-between text-sm font-ui"><span className="text-emerald-600">Code {coupon.code} — deposit removed</span><span className="text-emerald-600">−{fmtMoney(DEPOSIT)}</span></div>
                    : coupon.kind === "flat_rate"
                      ? <div data-testid="buddy-rate-side" className="flex items-center justify-between text-sm font-ui"><span className="text-emerald-600">Code {coupon.code} — buddy rate {fmtMoney(coupon.daily_rate)}/day</span><span className="text-emerald-600">deposit removed</span></div>
                      : <div className="flex items-center justify-between text-sm font-ui"><span className="text-emerald-600">{coupon.loyalty ? `${coupon.code} loyalty applied` : `Discount code ${coupon.code}`} (−{coupon.percent}%)</span><span className="text-emerald-600">−{fmtMoney(discountAmount)}</span></div>)}
                  <div className="flex items-center justify-between text-sm font-ui"><span className="text-muted-foreground">VAT (10%)</span><span>{fmtMoney(vatAmount)}</span></div>
                  <div className="flex items-center justify-between text-sm font-ui"><span className="text-muted-foreground">Processing fee (5%)</span><span>{fmtMoney(processingFee)}</span></div>
                  {form.dropoff_location === AIRPORT && <div data-testid="booking-airport-fee-row" className="flex items-center justify-between text-sm font-ui"><span className="text-muted-foreground">Airport drop-off fee</span><span>{fmtMoney(25)}</span></div>}
                  {depositWaived ? (
                    <div data-testid="deposit-waived-line" className="flex items-center justify-between text-sm font-ui"><span className="text-emerald-600">Security deposit — {(coupon?.kind === "waive_deposit" || coupon?.kind === "flat_rate") ? `removed by code ${coupon.code}` : "waived · Platinum Islander perk"}</span><span className="text-emerald-600"><s className="text-muted-foreground mr-1.5">{fmtMoney(DEPOSIT)}</s>{fmtMoney(0)}</span></div>
                  ) : (
                    <div className="flex items-center justify-between text-sm font-ui"><span className="text-muted-foreground">Refundable deposit — card hold at booking{isPlatinum ? " · reduced Platinum rate" : ""} (released at return)</span><span>{fmtMoney(DEPOSIT)}</span></div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-border"><span className="font-ui font-semibold">Due today</span><span className="gold-text font-ui text-2xl font-semibold">{fmtMoney(total)}</span></div>
                </div>
              )}
              <Button data-testid="booking-continue-button" disabled={!step1Valid} onClick={() => setStep(2)} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-8">
                Continue <ArrowRight size={15} className="ml-2" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <div data-testid="booking-step-2" className="space-y-6">
              {!user && (
                <p className="text-xs text-muted-foreground font-ui">
                  Booking as guest. <Link to="/login" className="gold-text hover:underline">Sign in</Link> to track your bookings.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label className="font-ui text-sm tracking-wide text-muted-foreground">Full name</Label>
                  <Input data-testid="booking-customer-name" value={form.customer_name} onChange={set("customer_name")} placeholder="Jordan Smith" className="input-dark mt-2" />
                </div>
                <div>
                  <Label className="font-ui text-sm tracking-wide text-muted-foreground">Email</Label>
                  <Input data-testid="booking-customer-email" type="email" value={form.customer_email} onChange={set("customer_email")} onBlur={(e) => { const em = e.target.value.trim(); if (/\S+@\S+\.\S+/.test(em)) api.get("/loyalty/status", { params: { email: em } }).then((r) => { setLoyalty(r.data); if (r.data.percent > 0) setCoupon((cur) => (cur && cur.percent >= r.data.percent ? cur : { code: r.data.tier, percent: r.data.percent, description: "Loyalty reward", loyalty: true })); }).catch(() => {}); }} placeholder="you@email.com" className="input-dark mt-2" />
                </div>
                <div>
                  <Label className="font-ui text-sm tracking-wide text-muted-foreground">Phone / WhatsApp</Label>
                  <Input data-testid="booking-customer-phone" value={form.customer_phone} onChange={set("customer_phone")} placeholder="+1 (555) 000-0000" className="input-dark mt-2" />
                </div>
                <div>
                  <Label className="font-ui text-sm tracking-wide text-muted-foreground">Flight # {renterType === "international" ? "(required)" : "(optional)"}</Label>
                  <Input data-testid="booking-flight-number" value={form.flight_number} onChange={set("flight_number")} placeholder="AA 1234" className="input-dark mt-2" />
                </div>
              </div>
              <div>
                <Label className="font-ui text-sm tracking-wide text-muted-foreground">Notes (optional)</Label>
                <Textarea data-testid="booking-notes" value={form.notes} onChange={set("notes")} placeholder="Child seat, late arrival, hotel name…" className="input-dark mt-2" />
              </div>
              <div>
                <Label className="font-ui text-sm tracking-wide text-muted-foreground">Renter type</Label>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  {[{ id: "international", t: "International visitor" }, { id: "local", t: "Local resident" }].map(({ id, t }) => (
                    <button key={id} type="button" data-testid={`renter-type-${id}`} onClick={() => setRenterType(id)} className={`rounded-xl border p-3.5 text-sm font-ui transition-colors duration-200 ${renterType === id ? "border-primary bg-primary/5 text-foreground font-semibold" : "border-border text-muted-foreground hover:border-primary/40"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="font-ui text-sm tracking-wide text-muted-foreground">Where are you from?</Label>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <select data-testid="booking-renter-country" value={form.renter_country} onChange={(e) => setForm({ ...form, renter_country: e.target.value, renter_state: "", renter_city: "" })} className="h-10 rounded-md input-dark border px-3 text-sm bg-card">
                    <option value="">Country</option>
                    {renterCountries.map((c) => <option key={c.isoCode} value={c.isoCode}>{c.name}</option>)}
                  </select>
                  <select data-testid="booking-renter-state" value={form.renter_state} onChange={(e) => setForm({ ...form, renter_state: e.target.value, renter_city: "" })} disabled={!form.renter_country || renterStates.length === 0} className="h-10 rounded-md input-dark border px-3 text-sm bg-card disabled:opacity-50">
                    <option value="">{renterStates.length === 0 && form.renter_country ? "No states — pick city" : "State / Island"}</option>
                    {renterStates.map((s) => <option key={s.isoCode} value={s.isoCode}>{s.name}</option>)}
                  </select>
                  <select data-testid="booking-renter-city" value={form.renter_city} onChange={set("renter_city")} disabled={!form.renter_country || (renterStates.length > 0 && !form.renter_state)} className="h-10 rounded-md input-dark border px-3 text-sm bg-card disabled:opacity-50">
                    <option value="">City</option>
                    {renterCities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div className="mt-4">
                  <Label className="font-ui text-sm tracking-wide text-muted-foreground">Street address (include town / island)</Label>
                  <Input data-testid="booking-address-input" value={form.address_line} onChange={set("address_line")} placeholder={form.renter_country === "BS" ? "e.g. 12 West Bay Street, Nassau, New Providence" : "e.g. 123 Main Street, Apt 4B"} className="input-dark mt-2" />
                  <p className="mt-1.5 text-xs text-muted-foreground">Street, town/city and {form.renter_country === "BS" ? "island" : "state/province"} — printed on your rental contract.</p>
                </div>
              </div>
              {localMinBlocked && (
                <p data-testid="booking-local-min-warning" className="text-sm text-amber-700 font-ui bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  Local Bahamian rentals require a 2-day minimum — please extend your dates. Your booking is verified with your driver's license upload below.
                </p>
              )}
              {saturdayPickup && (
                <p data-testid="booking-saturday-warning" className="text-sm text-amber-700 font-ui bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  Saturday pickups are unavailable — our office is closed Saturdays. Please choose another pickup date (Saturday returns are welcome).
                </p>
              )}
              {additionalDrivers > 0 && (
                <div className="space-y-4">
                  <Label className="font-ui text-sm tracking-wide text-muted-foreground">Additional driver details (each must be 25+ and present a license at pickup)</Label>
                  {Array.from({ length: additionalDrivers }, (_, i) => {
                    const d = driverDetails[i] || {};
                    const setD = (key) => (e) => {
                      const next = [...driverDetails];
                      next[i] = { ...next[i], [key]: e.target.value };
                      setDriverDetails(next);
                    };
                    return (
                      <div key={i} data-testid={`driver-slot-${i}`} className="bg-card border border-border rounded-xl p-4 space-y-3">
                        <p className="text-xs font-ui font-semibold text-primary uppercase tracking-[0.12em]">Driver {i + 2}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Input data-testid={`driver-${i}-name`} placeholder="Full name" value={d.name || ""} onChange={setD("name")} className="input-dark h-10" />
                          <Input data-testid={`driver-${i}-email`} type="email" placeholder="Email address" value={d.email || ""} onChange={setD("email")} className="input-dark h-10" />
                          <Input data-testid={`driver-${i}-license`} placeholder="Driver's license number" value={d.license_number || ""} onChange={setD("license_number")} className="input-dark h-10" />
                          {renterType === "international" && (
                            <>
                              <Input data-testid={`driver-${i}-passport`} placeholder="Passport number" value={d.passport_number || ""} onChange={setD("passport_number")} className="input-dark h-10" />
                              <Input data-testid={`driver-${i}-flight`} placeholder="Flight number (e.g. AA 100)" value={d.flight_number || ""} onChange={setD("flight_number")} className="input-dark h-10" />
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {renterType === "international" ? (
                <>
                  <div>
                    <Label className="font-ui text-sm tracking-wide text-muted-foreground">Passport number (required)</Label>
                    <Input data-testid="booking-passport-number" value={form.passport_number} onChange={set("passport_number")} placeholder="e.g. 123456789" className="input-dark mt-2" />
                  </div>
                  <div>
                    <Label className="font-ui text-sm tracking-wide text-muted-foreground">Passport photo (required — JPG, PNG or PDF)</Label>
                    <Input data-testid="booking-passport-upload" type="file" accept="image/*,.pdf" onChange={(e) => setPassportFile(e.target.files?.[0] || null)} className="input-dark mt-2 file:text-primary file:font-ui file:mr-3" />
                    <p className="mt-1.5 text-xs text-muted-foreground">Photo page of your passport. Uploaded securely and attached to your booking.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <Label className="font-ui text-sm tracking-wide text-muted-foreground">Driver's license — front (required)</Label>
                      <Input data-testid="booking-license-front-upload" type="file" accept="image/*,.pdf" onChange={(e) => setLicenseFront(e.target.files?.[0] || null)} className="input-dark mt-2 file:text-primary file:font-ui file:mr-3" />
                    </div>
                    <div>
                      <Label className="font-ui text-sm tracking-wide text-muted-foreground">Driver's license — back (required)</Label>
                      <Input data-testid="booking-license-back-upload" type="file" accept="image/*,.pdf" onChange={(e) => setLicenseBack(e.target.files?.[0] || null)} className="input-dark mt-2 file:text-primary file:font-ui file:mr-3" />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">International renters need a valid driver's license from their home country alongside their passport.</p>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <Label className="font-ui text-sm tracking-wide text-muted-foreground">Driver's license — front (required)</Label>
                      <Input data-testid="booking-license-front-upload" type="file" accept="image/*,.pdf" onChange={(e) => setLicenseFront(e.target.files?.[0] || null)} className="input-dark mt-2 file:text-primary file:font-ui file:mr-3" />
                    </div>
                    <div>
                      <Label className="font-ui text-sm tracking-wide text-muted-foreground">Driver's license — back (required)</Label>
                      <Input data-testid="booking-license-back-upload" type="file" accept="image/*,.pdf" onChange={(e) => setLicenseBack(e.target.files?.[0] || null)} className="input-dark mt-2 file:text-primary file:font-ui file:mr-3" />
                    </div>
                  </div>
                  <div>
                    <Label className="font-ui text-sm tracking-wide text-muted-foreground">NIB card (required)</Label>
                    <Input data-testid="booking-nib-upload" type="file" accept="image/*,.pdf" onChange={(e) => setNibFile(e.target.files?.[0] || null)} className="input-dark mt-2 file:text-primary file:font-ui file:mr-3" />
                    <p className="mt-1.5 text-xs text-muted-foreground">Photo of your National Insurance Board card — used to verify local Bahamian rentals.</p>
                  </div>
                  <div>
                    <Label className="font-ui text-sm tracking-wide text-muted-foreground">Credit card used for payment (required)</Label>
                    <Input data-testid="booking-card-photo-upload" type="file" accept="image/*" capture="environment" onChange={(e) => setCardPhoto(e.target.files?.[0] || null)} className="input-dark mt-2 file:text-primary file:font-ui file:mr-3" />
                    <p className="mt-1.5 text-xs text-muted-foreground">Photo of the card you'll pay with — cover the middle digits; we only need the name and last 4.</p>
                  </div>
                </>
              )}
              <div>
                <Label className="font-ui text-sm tracking-wide text-muted-foreground">Referral code (optional)</Label>
                <Input data-testid="booking-referral-code" value={form.referral_code} onChange={set("referral_code")} placeholder="A friend's member code — you both save" className="input-dark mt-2" />
              </div>
              <div className="flex items-start gap-3">
                <Checkbox data-testid="booking-age-confirm" checked={ageConfirmed} onCheckedChange={(v) => setAgeConfirmed(!!v)} className="mt-0.5 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                <Label className="text-sm text-muted-foreground leading-relaxed cursor-pointer" onClick={() => setAgeConfirmed(!ageConfirmed)}>
                  I confirm I am <span className="text-foreground font-semibold">25 years of age or older</span> and hold a valid driver's license (held for at least 2 years).
                </Label>
              </div>
              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(1)} className="rounded-full border-border bg-transparent hover:bg-black/5 hover:text-primary font-ui">
                  <ArrowLeft size={15} className="mr-2" /> Back
                </Button>
                <Button data-testid="booking-details-continue-button" disabled={!step2Valid} onClick={() => setStep(3)} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-8">
                  Continue to Payment <ArrowRight size={15} className="ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="booking-step-3" className="space-y-6">
              <div className="bg-card border border-border rounded-2xl p-6 text-sm space-y-2">
                <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3">Summary</p>
                <p>{vehicle.name} — {fmtDate(form.start_date)}{form.pickup_time ? ` · ${form.pickup_time}` : ""} → {fmtDate(form.end_date)}{form.dropoff_time ? ` · ${form.dropoff_time}` : ""}</p>
                <p data-testid="booking-summary-specs" className="text-muted-foreground flex items-center gap-4">
                  <span className="flex items-center gap-1.5"><Users size={13} className="gold-text" /> {vehicle.seats} seats</span>
                  <span className="flex items-center gap-1.5"><Luggage size={13} className="gold-text" /> Fits {vehicle.luggage ?? 0} bags</span>
                </p>
                <p className="text-muted-foreground">{form.pickup_location} → {form.dropoff_location}</p>
                <p className="text-muted-foreground">{form.customer_name} · {form.customer_email}</p>
                {babySeat && <p className="text-muted-foreground">+ Baby car seat ({days >= 12 ? "FREE — 12+ day perk" : fmtMoney(babySeatFee)})</p>}
                {gps && <p className="text-muted-foreground">+ GPS navigation ({fmtMoney(gpsFee)})</p>}
                {sli && <p data-testid="booking-sli-summary" className="text-muted-foreground">+ SLI liability insurance ({fmtMoney(sliFee)})</p>}
                {roadside && <p data-testid="booking-roadside-step3" className="text-muted-foreground">+ 24/7 roadside assistance ({fmtMoney(roadsideFee)})</p>}
                {additionalDrivers > 0 && <p className="text-muted-foreground">+ {additionalDrivers} additional driver{additionalDrivers > 1 ? "s" : ""} ({fmtMoney(driverFee)})</p>}
                {weeklyDiscount > 0 && <p data-testid="booking-weekly-discount-step3" className="text-emerald-600">Weekly discount (7+ days): −{fmtMoney(weeklyDiscount)}</p>}
                {coupon && (coupon.kind === "waive_deposit" || coupon.kind === "flat_rate"
                  ? <p data-testid="booking-coupon-waive-line" className="text-emerald-600">Code {coupon.code}: {coupon.kind === "flat_rate" ? `buddy rate ${fmtMoney(coupon.daily_rate)}/day — security deposit removed` : "security deposit removed"}</p>
                  : <p className="text-emerald-600">{coupon.loyalty ? `${coupon.code} loyalty` : `Discount code ${coupon.code}`}: −{fmtMoney(discountAmount)}</p>)}
                {referral && <p data-testid="booking-referral-line" className="text-emerald-600">Referral {referralCode.toUpperCase()}: −{fmtMoney(referralDiscount)}</p>}
                {loyalty?.tier === "Platinum Islander" && (gps || babySeat) && (
                  <p data-testid="platinum-perks-line" className="text-emerald-600">Platinum perks applied: free GPS unit + free baby car seat (subject to availability)</p>
                )}
                {loyalty?.tier === "Gold Voyager" && (gps || babySeat) && (
                  <p data-testid="gold-perks-line" className="text-emerald-600">Gold perks applied: half-price GPS unit + baby car seat (subject to availability)</p>
                )}
                <p className="text-muted-foreground">+ VAT 10% ({fmtMoney(vatAmount)}) + Processing fee 5% ({fmtMoney(processingFee)})</p>
                {depositWaived ? (
                  <p data-testid="deposit-waived-step3" className="text-emerald-600">+ Refundable deposit — {coupon?.kind === "waive_deposit" ? `removed by code ${coupon.code}` : "waived · Platinum Islander perk"} ({fmtMoney(0)})</p>
                ) : (
                  <p className="text-muted-foreground">+ Refundable deposit — card hold at booking, not charged{isPlatinum ? " · reduced Platinum rate" : ""} ({fmtMoney(DEPOSIT)})</p>
                )}
                <p className="gold-text font-ui text-xl font-semibold pt-2">Due today: {fmtMoney(total)}</p>
              </div>

              <div className="bg-card border border-border rounded-2xl p-5">
                <Label className="font-ui text-sm tracking-wide text-muted-foreground">Have a discount code?</Label>
                <div className="mt-2 flex gap-3">
                  <Input data-testid="coupon-code-input" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="e.g. ISLAND15" className="input-dark uppercase" disabled={!!coupon} />
                  {coupon ? (
                    <Button type="button" variant="outline" onClick={() => { setCoupon(null); setCouponCode(""); }} className="rounded-full border-border bg-transparent hover:bg-black/5 font-ui shrink-0">
                      Remove
                    </Button>
                  ) : (
                    <Button data-testid="coupon-apply-button" type="button" onClick={applyCoupon} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui shrink-0">
                      Apply
                    </Button>
                  )}
                </div>
                {coupon && (
                  <p data-testid="coupon-applied-message" className="mt-2 text-sm text-emerald-600 font-ui">
                    {coupon.code} applied — {coupon.percent}% off{coupon.description ? ` · ${coupon.description}` : ""}
                  </p>
                )}
              </div>

              <div className="bg-card border border-border rounded-2xl p-5">
                <Label className="font-ui text-sm tracking-wide text-muted-foreground">Referred by a member? Enter their code (e.g. EDL-1A2B3C)</Label>
                <div className="mt-2 flex gap-3">
                  <Input data-testid="referral-code-input" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} placeholder="EDL-XXXXXX" className="input-dark uppercase" disabled={!!referral} />
                  {referral ? (
                    <Button type="button" variant="outline" onClick={() => { setReferral(null); setReferralCode(""); }} className="rounded-full border-border bg-transparent hover:bg-black/5 font-ui shrink-0">
                      Remove
                    </Button>
                  ) : (
                    <Button data-testid="referral-apply-button" type="button" onClick={applyReferral} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui shrink-0">
                      Apply
                    </Button>
                  )}
                </div>
                {referral && (
                  <p data-testid="referral-applied-message" className="mt-2 text-sm text-emerald-600 font-ui">
                    $25 off applied — and your friend earns $25 credit when your rental completes.
                  </p>
                )}
              </div>

              <div>
                <p className="text-xs font-ui text-emerald-600 flex items-center gap-1.5 mb-3">
                  <ShieldCheck size={13} /> Secure checkout — card, Google Pay, Apple Pay and PayPal confirm instantly. A 5% processing fee applies to all payment methods.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { id: "card", icon: CreditCard, t: "Card / Google Pay / Apple Pay", d: "Secure Stripe checkout — Google Pay & Apple Pay supported, instant confirmation (5% fee)" },
                  { id: "paypal", icon: Wallet, t: "Pay by Card / PayPal", d: "Cards accepted via PayPal — no PayPal account needed, instant confirmation (5% fee)" },
                  ...(wireEnabled ? [{ id: "wire", icon: Landmark, t: "Bank Wire Transfer", d: "Wire the total to our bank account — we confirm your booking once the funds arrive (5% fee)" }] : []),
                ].map(({ id, icon: Icon, t, d }) => (
                  <button
                    key={id}
                    data-testid={`payment-method-${id}`}
                    onClick={() => setMethod(id)}
                    className={`text-left rounded-2xl border p-5 transition-colors duration-200 ${
                      method === id ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    <Icon size={20} className="gold-text" />
                    <p className="mt-3 font-ui font-semibold text-sm">{t}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{d}</p>
                  </button>
                ))}
              </div>

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(2)} className="rounded-full border-border bg-transparent hover:bg-black/5 hover:text-primary font-ui">
                  <ArrowLeft size={15} className="mr-2" /> Back
                </Button>
                <Button
                  data-testid="booking-submit-button"
                  disabled={submitting}
                  onClick={submit}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-8"
                >
                  {submitting ? "Processing…" : method === "card" || method === "paypal" ? `Pay ${fmtMoney(total)}${method === "paypal" ? " by Card / PayPal" : ""}` : `Reserve — Pay via ${{ manual_card: "Manual Card", zelle: "Zelle", wire: "Bank Wire" }[method] || "Bank Wire"}`}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground font-ui">
                Cancellations carry a 20% charge; within 24 hours of pickup they are non-refundable. The refundable deposit ($150, or $100 for Platinum members) is a card hold placed at booking — released when the vehicle is returned in good condition, with any penalties deducted from the hold and the balance invoiced.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
